import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICheckoutReducer } from 'modules/ScrShopNew/store/slices/checkout/types';
import {
  getCheckoutSummaryFromStorage,
  saveCheckoutSummaryToStorage
} from 'modules/ScrShopNew/store/utils/checkoutStorage';

const initialState: ICheckoutReducer = {
  createdAt: '',
  currency: 'EUR',
  discountTotal: 0,
  grandTotal: 0,
  grossProductPrice: 0,
  grossShippingPrice: 0,
  productDiscount: 0,
  productPrice: 0,
  productQuantity: 0,
  productTax: 0,
  productTotal: 0,
  products: [],
  shippingDiscount: 0,
  shippingPrice: 0,
  shippingTax: 0,
  shippingTaxRate: 0,
  shippingTotal: 0,
  status: '',
  taxTotal: 0,
  updatedAt: '',
  _collection: '',
  _id: '',
  _laboratory: '',
  _user: '',
  _endCustomer: '',
  partialShippingCosts: [],
  _coupon: {
    active: false,
    code: '',
    codeLowerCase: '',
    description: null,
    discounts: [],
    endCustomer: null,
    minimumOrderValue: 0,
    name: '',
    sourceCoupon: null,
    validUntilDate: '',
    _id: '',
    _user: ''
  },
  // New
  grandProductGrossPrice: 0,
  grandShippingGrossPrice: 0,
  grandShippingPrice: 0,
  grandProductTax: 0,
  grandTax: 0,
  checkoutSummary: {
    _id: '',
    products: [],
    currency: 'EUR',
    grandTotal: 0,
    grandTax: 0,
    grandProductGrossPrice: 0,
    grandShippingGrossPrice: 0,
    grandShippingPrice: 0,
    grandProductTax: 0,
    productDiscount: 0,
    discountTotal: 0,
    shippingTaxRate: 0,
    coupon: {
      active: false,
      code: '',
      codeLowerCase: '',
      description: null,
      discounts: [],
      endCustomer: null,
      minimumOrderValue: 0,
      name: '',
      sourceCoupon: null,
      validUntilDate: '',
      _id: '',
      _user: ''
    },
    partialShippingCosts: [],
    gtcAndPrivacyCheckbox: {
      value: false,
      error: ''
    },
    disableCouponActions: false,
    showGtcAndPrivacyCheckbox: false,
    disableSubmitSection: false,
    lastUpdated: new Date().toISOString(),
    _shoppingCart: ''
  }
};

const updateCheckoutSummary = (state: ICheckoutReducer) => {
  state.checkoutSummary = {
    ...state.checkoutSummary,
    _id: state._id,
    products: state.products,
    currency: state.currency,
    grandTotal: state.grandTotal,
    grandTax: state.grandTax,
    grandProductGrossPrice: state.grandProductGrossPrice,
    grandShippingGrossPrice: state.grandShippingGrossPrice,
    grandShippingPrice: state.grandShippingPrice,
    grandProductTax: state.grandProductTax,
    productDiscount: state.productDiscount,
    discountTotal: state.discountTotal,
    shippingTaxRate: state.shippingTaxRate,
    coupon: state._coupon,
    partialShippingCosts: state.partialShippingCosts,
    lastUpdated: new Date().toISOString()
  };

  saveCheckoutSummaryToStorage(state.checkoutSummary);
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    addUpdateProductToCartSuccessAction(state, action: PayloadAction<ICheckoutReducer>) {
      Object.assign(state, action.payload);
      updateCheckoutSummary(state);
    },
    updateCheckoutStoreAction(state, action: PayloadAction<Partial<ICheckoutReducer>>) {
      Object.assign(state, action.payload);
      updateCheckoutSummary(state);
    },
    updateCheckoutSummaryUIAction(
      state,
      action: PayloadAction<Partial<ICheckoutReducer['checkoutSummary']>>
    ) {
      state.checkoutSummary = {
        ...state.checkoutSummary,
        ...action.payload,
        lastUpdated: new Date().toISOString()
      };

      saveCheckoutSummaryToStorage(state.checkoutSummary);
    },
    cleanUpCheckoutAction(state) {
      const { checkoutSummary } = state;
      Object.assign(state, initialState);

      state.checkoutSummary = checkoutSummary;
    },
    cleanUpCheckout(state) {
      Object.assign(state, initialState);
      updateCheckoutSummary(state);
    },
    cleanUpCheckoutStorageAction() {
      localStorage.removeItem(`checkout_${window.SITE_ID}`);
    },
    setCollectionAction(state) {
      const persistData = localStorage.getItem(`checkout_${window.SITE_ID}`);
      const persistState = persistData ? JSON.parse(persistData) : initialState;

      const storedCheckoutSummary = getCheckoutSummaryFromStorage();

      if (storedCheckoutSummary) {
        persistState.checkoutSummary = storedCheckoutSummary;
      }

      return { ...state, ...persistState };
    },
    restoreCheckoutSummaryFromStorageAction(state) {
      const storedCheckoutSummary = getCheckoutSummaryFromStorage();

      if (storedCheckoutSummary) {
        state.checkoutSummary = {
          ...state.checkoutSummary,
          ...storedCheckoutSummary,
          lastUpdated: new Date().toISOString()
        };
      }
    },
    clearCheckoutSummaryStorageAction() {
      try {
        localStorage.removeItem(`checkout_summary_${window.SITE_ID}`);
      } catch (error) {
        console.warn('Failed to clear checkout summary from localStorage:', error);
      }
    }
  }
});

export const {
  addUpdateProductToCartSuccessAction,
  updateCheckoutStoreAction,
  updateCheckoutSummaryUIAction,
  cleanUpCheckoutAction,
  cleanUpCheckout,
  cleanUpCheckoutStorageAction,
  setCollectionAction,
  restoreCheckoutSummaryFromStorageAction,
  clearCheckoutSummaryStorageAction
} = checkoutSlice.actions;

export const checkoutReducer = checkoutSlice.reducer;
